import { Injectable } from '@angular/core';
import { STORAGE_KEY } from '@cpd-app/core-module/constants/storage-key.const';
import {
  AuthDataService,
  NavigationService
} from '@cpd-app/core-module/services';
import { User } from '@cpd-app/generated';
import { ErrorCodeType } from '@cpd-app/shared-module/constants';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { AppState } from '..';
import { deriveRestoredAuthorization } from './auth-restore.util';
import { CognitoAuthActions, CognitoAuthFacade } from '../cognito-auth';
import { GoalActions } from '../goal';
import { LoaderFacade } from '../loader';
import { UserProfileActions, UserProfileFacade } from '../user-profile';
import * as AuthActions from './auth.actions';
import * as AuthSelectors from './auth.selectors';
import { PosthogService } from '@cpd-app/core-module/services/posthog/posthog.service';
import { staffLandingUrl } from './staff-landing.util';

@Injectable()
export class AuthEffects {
  /*
  Allows exactly one automatic session re-mint per page load when both the CPD
  session token and the refresh token are dead but the Cognito session is still
  valid. One-shot so a backend that keeps rejecting cannot cause a dispatch loop.
  */
  private sessionRecoveryAttempted = false;

  public initSessionState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.initAuthState),
      map(() => {
        const token = sessionStorage.getItem(STORAGE_KEY.TOKEN);
        this.store.dispatch(
          AuthActions.setSessionInitializing({ initializing: true })
        );

        return AuthActions.getSession({ token });
      })
    );
  });

  public getSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getSession),
      switchMap(({ token }) => {
        return this.authDataService.getSession(token).pipe(
          concatMap((sessionStateInput) => {
            return [
              AuthActions.saveTokenToStorage({
                token: sessionStateInput.token
              }),
              AuthActions.saveTokenToState({
                token: sessionStateInput.token
              }),
              CognitoAuthActions.saveTokenToStorage({
                token: sessionStateInput.token,
                refreshToken: sessionStateInput.refreshToken
              }),
              UserProfileActions.saveUserProfileToState({
                userProfile: sessionStateInput.userProfile
              }),
              AuthActions.restoreAuthorizationState(
                deriveRestoredAuthorization(
                  sessionStateInput.userProfile?.userRole,
                  sessionStateInput.isAuthenticated
                )
              ),
              AuthActions.getSessionSuccessProfileWorkTypeSet({
                profileWorkTypeSet:
                  !!sessionStateInput.userProfile.profileWorkType?.type
              }),
              AuthActions.getSessionSuccess(),
              GoalActions.setReportDownloaded({
                reportDownloaded: sessionStateInput.userProfile.reportDownloaded
              }),
              GoalActions.setReportShared({
                reportShared: sessionStateInput.userProfile.reportShared
              })
            ];
          }),
          catchError((error) => {
            return of(AuthActions.refreshSessionAndRetryGetSession({ error }));
          })
        );
      })
    );
  });

  public refreshSessionAndRetryGetSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.refreshSessionAndRetryGetSession),
      switchMap(({ error }) => {
        const refreshToken = localStorage.getItem(STORAGE_KEY.REFRESH_TOKEN);

        if (!refreshToken) {
          const errorMessage = error.message || 'Unknown error occurred';
          return of(AuthActions.getSessionFailed({ errorMessage }));
        }

        return this.authDataService.refreshSession(refreshToken).pipe(
          concatMap((refreshResponse) => {
            const token = {
              token: refreshResponse.token,
              refreshToken: refreshResponse.refreshToken
            };

            return [
              AuthActions.saveTokenToStorage(token),
              AuthActions.saveTokenToState(token),
              CognitoAuthActions.saveTokenToStorage(token),
              AuthActions.getSession({ token: refreshResponse.token })
            ];
          }),
          catchError((refreshError) => {
            const errorMessage =
              refreshError.message || 'Unknown error occurred';
            return of(AuthActions.retrySessionFailed({ errorMessage }));
          })
        );
      })
    );
  });

  public sessionSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getSessionSuccess),
      debounceTime(200),
      map(() => {
        return AuthActions.setSessionInitializing({ initializing: false });
      }),
      tap(() => this.navigationService.goToDirectLink())
    );
  });

  public adminLogIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.adminLogIn),
      switchMap(({ username, password }) => {
        this.loaderFacade.showLoaderUntil(AuthActions.adminLogInSuccess.type);
        return this.authDataService.adminLogIn(username, password).pipe(
          switchMap((user: User) => [AuthActions.adminLogInSuccess({ user })]),
          catchError((error: TypeError) => {
            this.loaderFacade.hideLoader();
            return of(AuthActions.adminLogInFailure({ error }));
          })
        );
      })
    );
  });

  public adminLogInSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.adminLogInSuccess),
      withLatestFrom(
        this.store.pipe(select(AuthSelectors.selectLoginWallRedirectUrl))
      ),
      // STAFF-1. Straight into the one staff workspace, for every staff
      // role: there is no dashboard picker any more.
      tap(([, url]) => {
        // RELEASE-1. A staff session has no refresh token by design, so one
        // still in storage belongs to an earlier learner session in this
        // browser, possibly expired by the password reset that led here.
        localStorage.removeItem(STORAGE_KEY.REFRESH_TOKEN);
        this.navigationService.goToStoredUrl(staffLandingUrl(url));
      }),
      switchMap(([{ user }]) => [
        AuthActions.saveTokenToStorage({ token: user.token })
      ])
    );
  });

  public adminLogOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.adminLogOut),
      switchMap(() => [
        AuthActions.deleteTokenFromStorage(),
        UserProfileActions.deleteUserProfile(),
        AuthActions.adminLogOutSuccess()
      ])
    )
  );

  public adminLogOutSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.adminLogOutSuccess),
      switchMap(() => {
        this.navigationService.setDirectLink('');
        /*
         * STAFF-3 (L1). Leave nothing of this person behind, as the Cognito
         * sign-out does: stop tracking them, and clear what this browser kept
         * for them. Runs after deleteTokenFromStorage$, and before
         * initAuthState mints the next (anonymous) session.
         */
        this.posthogService?.reset();
        localStorage.clear();

        return [
          AuthActions.setLoginWallRedirectUrl({ loginWallRedirectUrl: '' }),
          AuthActions.initAuthState()
        ];
      }),
      tap(() => this.navigationService.goToHomePage())
    );
  });

  public returnSessionProcessedSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.returnSessionProcessedSuccess),
      switchMap(() => [
        AuthActions.initAuthState(),
        UserProfileActions.loadShareUserProfile()
      ])
    );
  });

  public saveTokenToStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.saveTokenToStorage),
        tap(({ token, refreshToken }) => {
          if (token) {
            sessionStorage.setItem(STORAGE_KEY.TOKEN, token);
          }

          /*
          The action has always carried a refreshToken and this effect has
          always ignored it, so persisting the pair only worked where the caller
          ALSO remembered to dispatch CognitoAuthActions.saveTokenToStorage.
          `CognitoAuthEffects.getSession$` does not: it calls
          `authFacade.setTokenToStorage(token, refreshToken)` and the refresh
          token was dropped on the floor, leaving a session that could never
          renew itself. Honour the payload (CPD-652).
          */
          if (refreshToken) {
            localStorage.setItem(STORAGE_KEY.REFRESH_TOKEN, refreshToken);
          }
        })
      );
    },
    { dispatch: false }
  );

  public deleteTokenFromStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.deleteTokenFromStorage),
        /*
        Both halves of the session go, not just the session token. Clearing
        only sessionStorage left the refresh token behind on every path that
        dispatches this WITHOUT also dispatching
        CognitoAuthActions.deleteTokenFromStorage -- adminLogOut$ (the
        analytics and manager headers) and the Cognito recovery branches of
        getSessionFailed$ / retrySessionFailed$ below. A refresh token that
        outlives its session is what pins a user to the 403 page, because
        SessionInterceptor.hasSessionEvidence() reads it as a live session
        (CPD-652).

        Safe on the recovery branches: the token being dropped has already
        been rejected by the server, and a successful re-mint writes a fresh
        pair through saveTokenToStorage.
        */
        tap(() => {
          const deadToken = sessionStorage.getItem(STORAGE_KEY.TOKEN);

          sessionStorage.removeItem(STORAGE_KEY.TOKEN);
          localStorage.removeItem(STORAGE_KEY.REFRESH_TOKEN);

          /*
          THE POINTER TO THE DEAD SESSION HAS TO GO TOO.

          `CognitoAuthEffects.saveTokenToStorage$` mirrors the session token
          into localStorage under `token_<userId>`, and
          `checkAuthenticationStatusSuccess$` copies it straight back into
          sessionStorage on every page load. sessionStorage is per-tab, so that
          copy is what gives a NEW TAB a session at all -- and it is also what
          kept handing every new tab the same token we had just established was
          dead. The load then failed, and the member was back on the 403 page
          they had just tried to escape (CPD-652).

          Matched BY VALUE rather than by key. The key needs a userId, which
          lives only in memory and is null on a fresh load and after a session
          has already failed, which is exactly when this action fires -- the
          same trap that once disabled the Cognito cleanup below. Matching on
          the value can only ever remove a pointer to the session being torn
          down here, so the magic link's `token_<email>` handoff, which holds a
          DIFFERENT and still-live guest token, is untouched.
          */
          if (deadToken) {
            Object.keys(localStorage)
              .filter(
                (key) =>
                  key.startsWith(`${STORAGE_KEY.TOKEN}_`) &&
                  localStorage.getItem(key) === deadToken
              )
              .forEach((key) => localStorage.removeItem(key));
          }
        })
      );
    },
    { dispatch: false }
  );

  public getSessionFailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getSessionFailed),
      withLatestFrom(this.cognitoAuthFacade.isAuthenticated$),
      switchMap(([, isCognitoAuthenticated]) => {
        if (this.tryRecoverSession(isCognitoAuthenticated)) {
          return [
            AuthActions.deleteTokenFromStorage(),
            UserProfileActions.deleteUserProfile(),
            // Must be cleared before the re-mint: the CognitoCallback request
            // is not on the X-Skip-Session-Check list, so SessionInterceptor
            // would park it behind this very flag and deadlock the recovery.
            AuthActions.setSessionInitializing({ initializing: false }),
            CognitoAuthActions.getSession()
          ];
        }

        this.loaderFacade.hideLoader();
        console.error('Session expired');
        this.navigationService.goToErrorPage(ErrorCodeType.SessionExpired);

        return [
          AuthActions.deleteTokenFromStorage(),
          CognitoAuthActions.deleteTokenFromStorage(),
          UserProfileActions.deleteUserProfile(),
          AuthActions.setSessionInitializing({ initializing: false })
        ];
      })
    );
  });

  public retrySessionFailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.retrySessionFailed),
      withLatestFrom(this.cognitoAuthFacade.isAuthenticated$),
      switchMap(([, isCognitoAuthenticated]) => {
        if (this.tryRecoverSession(isCognitoAuthenticated)) {
          return [
            AuthActions.deleteTokenFromStorage(),
            UserProfileActions.deleteUserProfile(),
            // See getSessionFailed$: cleared so the re-mint's CognitoCallback
            // request is not parked behind the initializing flag.
            AuthActions.setSessionInitializing({ initializing: false }),
            CognitoAuthActions.getSession()
          ];
        }

        return [
          AuthActions.deleteTokenFromStorage(),
          CognitoAuthActions.deleteTokenFromStorage(),
          UserProfileActions.deleteUserProfile(),
          AuthActions.setSessionInitializing({ initializing: false })
        ];
      })
    );
  });

  /*
  If the one-shot recovery itself fails downstream (cognitoCallback rejects,
  or the Cognito status check errors), fall back to the ordinary failure
  handling instead of hanging with no session, no error page, and a consumed
  recovery flag. Scoped by the flag so normal sign-in failures are untouched.
  tryRecoverSession() already returns false on the second pass, so this cannot
  loop.
  */
  public sessionRecoveryFailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CognitoAuthActions.cognitoCallbackFailure,
        CognitoAuthActions.checkAuthenticationStatusFailure
      ),
      filter(() => this.sessionRecoveryAttempted),
      map(() =>
        AuthActions.getSessionFailed({
          errorMessage: 'Session recovery failed'
        })
      )
    );
  });

  /**
   * Decide whether a dead session should re-mint through the Cognito path
   * instead of dead-ending on the Session Expired page.
   *
   * CognitoAuthActions.getSession() creates a throwaway anonymous CPD session
   * (only to satisfy AuthGuard) and then runs cognitoCallback, which resolves
   * the EXISTING user by verified email from the Cognito idToken and mints a
   * real authenticated session for them. No profile data is lost.
   *
   * On the recovery path CognitoAuthActions.deleteTokenFromStorage must NOT be
   * dispatched: its effect calls cognitoAuthService.signOut(), which destroys
   * the Cognito session that recovery depends on.
   */
  private tryRecoverSession(isCognitoAuthenticated: boolean): boolean {
    if (!isCognitoAuthenticated || this.sessionRecoveryAttempted) {
      return false;
    }

    this.sessionRecoveryAttempted = true;
    return true;
  }

  public createReturnSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.createReturnSession),
      switchMap(({ emailAddress }) => {
        return this.authDataService
          .createReturnSession(emailAddress)
          .pipe(map(() => UserProfileActions.userProfileHasReturnLink()));
      })
    );
  });

  /*
   * AUTH.9. processReturnSession is handled by ReturnLinkEffects. This effect
   * used to call returnSession(returnToken), which minted a session AS the
   * member for whoever held an old link; under errorPolicy 'all' a refused
   * token came back as the string '-4', which this treated as a success and
   * stored as the share token -- the silent bounce on every expired link.
   */

  public submitPasscode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.submitPasscode),
      switchMap(({ passcode }) => {
        return this.authDataService.submitPasscode(passcode).pipe(
          map((isOverPasscode) => {
            if (isOverPasscode) {
              this.navigationService.goToHomePage();
              this.userProfileFacade.acceptPrivacyPolicy();

              return AuthActions.submitPasscodeSuccess();
            }
            return AuthActions.submitPasscodeFailure();
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private authDataService: AuthDataService,
    private store: Store<AppState>,
    private navigationService: NavigationService,
    private loaderFacade: LoaderFacade,
    private userProfileFacade: UserProfileFacade,
    private cognitoAuthFacade: CognitoAuthFacade,
    // STAFF-3. Trailing and optional so constructor-built specs keep compiling.
    private posthogService?: PosthogService
  ) {}
}
